import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Play, Pause, ChevronLeft, ChevronRight, Calendar, 
  Maximize2, Minimize2, Layers, Music, Volume2, VolumeX, 
  SkipForward, Sparkles, X, Tag 
} from 'lucide-react';
import apiClient from '../../services/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import { extractPhotosFromRecords } from '../../services/photoAlbumService';
import usePermissionStore from '../../stores/permissionStore';

// 定义切换效果配置
const TRANSITION_EFFECTS = {
  fade: {
    label: '淡入淡出',
    active: 'opacity-100 z-10',
    inactive: 'opacity-0 z-0'
  },
  zoom: {
    label: '深渊缩放',
    active: 'opacity-100 scale-100 z-10',
    inactive: 'opacity-0 scale-150 z-0'
  },
  flip: {
    label: '3D翻转',
    active: 'opacity-100 rotate-y-0 z-10',
    inactive: 'opacity-0 -rotate-y-90 z-0'
  },
  blur: {
    label: '梦幻模糊',
    active: 'opacity-100 blur-0 z-10',
    inactive: 'opacity-0 blur-xl z-0'
  }
};

// 音乐播放列表配置
const MUSIC_PLAYLIST = [
  { 
    id: 1, 
    title: "舒缓旋律", 
    bpm: 100,
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" 
  },
  { 
    id: 2, 
    title: "动感节拍", 
    bpm: 120,
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-8.mp3" 
  },
  { 
    id: 3, 
    title: "静谧钢琴", 
    bpm: 90,
    url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3" 
  }
];

/**
 * 时光相册播放器组件
 * 
 * @param {Object} props
 * @param {Array} props.photos - 照片数据数组 [{id, url, date, course_name, description}]
 * @param {Function} props.onClose - 关闭回调
 * @param {boolean} [props.autoPlay=true] - 是否自动播放
 */
export default function PhotoAlbumPlayer({ 
  photos = [], 
  onClose, 
  autoPlay = true 
}) {
  // === 状态管理 ===
  const [sortedPhotos, setSortedPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false); 
  const [progress, setProgress] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);
  
  // 图片加载失败跟踪
  const [brokenImageIds, setBrokenImageIds] = useState(new Set());
  
  // 标签筛选状态
  const [selectedTag, setSelectedTag] = useState("全部");
  // 多选年月筛选状态 (存储 YYYY-MM 格式)
  const [selectedMonths, setSelectedMonths] = useState(new Set());
  const [showTimeline, setShowTimeline] = useState(true); // 时间轴侧边栏显示状态

  const [effectMode, setEffectMode] = useState('smart'); // 固定智能节奏
  const [smartCurrentEffect, setSmartCurrentEffect] = useState('fade');
  const [hasStarted, setHasStarted] = useState(true); // 直接开始，无需提示
  
  const [isMusicOn, setIsMusicOn] = useState(false); // 默认关闭音乐
  const [currentMusicIndex, setCurrentMusicIndex] = useState(0); 
  const [currentDuration, setCurrentDuration] = useState(5000);
  const [baseDuration, setBaseDuration] = useState(5); // 基础时长（秒），用户可调
  const [statusMessage, setStatusMessage] = useState('');  
  const audioRef = useRef(null);

  // 触摸状态
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  
  // 获取当前学生信息
  const { studentId } = usePermissionStore();

  // === 动态提取可用标签（类型 + 课程名称）===
  const availableTags = useMemo(() => {
    const types = [...new Set(sortedPhotos.map(p => p.type).filter(Boolean))];
    const courses = [...new Set(sortedPhotos.map(p => p.course_name).filter(Boolean))];
    
    // 翻译类型名称，并过滤掉没有照片的类型
    const typeLabels = types
      .map(type => {
        if (type === 'artwork') return '作品';
        if (type === 'classroom') return '课堂';
        return type;
      })
      .filter(label => {
        // 检查该类型是否有照片
        const typeKey = label === '作品' ? 'artwork' : (label === '课堂' ? 'classroom' : label);
        return sortedPhotos.some(p => p.type === typeKey);
      });
    
    return ["全部", ...typeLabels, ...courses];
  }, [sortedPhotos]);
  
  // === 动态提取可用年月列表并转换为YYMM格式 ===
  const availableMonths = useMemo(() => {
    const months = [...new Set(sortedPhotos
      .filter(p => !brokenImageIds.has(p.id)) // 排除损坏的图片
      .map(p => {
        if (!p.date) return null;
        const date = new Date(p.date);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      })
      .filter(Boolean)
    )].sort().reverse(); // 从大到小排序（最新的在前）
    
    return months;
  }, [sortedPhotos, brokenImageIds]);
  
  // 将 YYYY-MM 格式转换为 YYMM 格式显示
  const formatMonthToYYMM = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    return `${year.slice(2)}${month}`;
  };

  // === 初始化 ===
  // 如果提供了 photos prop，直接使用
  useEffect(() => {
    if (photos && photos.length > 0) {
      const sorted = [...photos].sort((a, b) => new Date(a.date) - new Date(b.date));
      setSortedPhotos(sorted);
      setIsLoading(false);
    }
  }, [photos]);
  
  // 如果没有提供 photos，自动加载学生的考勤记录并提取照片
  useEffect(() => {
    // 只在以下条件满足时加载：
    // 1. 没有提供 photos prop 或 photos 为空
    // 2. 有 studentId
    // 3. sortedPhotos 还是空的（避免重复加载）
    // 4. 不在加载中
    // 5. 还没有尝试过加载（避免 401 错误后无限重试）
    if ((!photos || photos.length === 0) && studentId && sortedPhotos.length === 0 && !isLoading && !hasAttemptedLoad) {
      const loadPhotos = async () => {
        try {
          setIsLoading(true);
          setHasAttemptedLoad(true); // 标记为已尝试加载
          // 使用学生专用 API 端点避免 401 权限错误
          const response = await apiClient.get(API_ENDPOINTS.AUTH.ME_ATTENDANCE_RECORDS, {
            page: 1,
            per_page: 1000 // 获取所有记录
          });
          const records = response.items || [];
          const extractedPhotos = extractPhotosFromRecords(records);
          const sorted = [...extractedPhotos].sort((a, b) => new Date(a.date) - new Date(b.date));
          setSortedPhotos(sorted);
        } catch (error) {
          console.error('加载相册照片失败:', error);
          setIsLoading(false); // 确保错误时也解除加载状态
        } finally {
          setIsLoading(false);
        }
      };
      loadPhotos();
    }
  }, [studentId]); // 只依赖 studentId，移除 photos 避免无限循环

  // === 筛选逻辑 ===
  const filteredPhotos = useMemo(() => {
    // 首先过滤掉损坏的图片
    let filtered = sortedPhotos.filter(photo => !brokenImageIds.has(photo.id));
    
    // 按标签筛选
    if (selectedTag !== "全部") {
      if (selectedTag === '作品') {
        filtered = filtered.filter(photo => photo.type === 'artwork');
      } else if (selectedTag === '课堂') {
        filtered = filtered.filter(photo => photo.type === 'classroom');
      } else {
        filtered = filtered.filter(photo => photo.course_name === selectedTag);
      }
    }
    
    // 按多选月份筛选
    if (selectedMonths.size > 0) {
      filtered = filtered.filter(photo => {
        if (!photo.date) return false;
        const date = new Date(photo.date);
        const photoMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        return selectedMonths.has(photoMonth);
      });
    }
    
    return filtered;
  }, [selectedTag, selectedMonths, sortedPhotos, brokenImageIds]);

  // 当标签或选中月份改变时，重置当前索引到 0
  useEffect(() => {
    setCurrentIndex(0);
    setProgress(0);
    if (isPlaying) updateDynamicDuration();
  }, [selectedTag, selectedMonths]);

  // 状态信息自动清除
  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  // === 核心播放器逻辑 ===
  // 当基础时长改变时，更新当前时长
  useEffect(() => {
    if (!isMusicOn) {
      setCurrentDuration(baseDuration * 1000);
    }
  }, [baseDuration, isMusicOn]);
  
  useEffect(() => {
    let interval;
    if (isPlaying && filteredPhotos.length > 0) {
      const updateInterval = 50; 
      interval = setInterval(() => {
        setProgress((prev) => {
          const increment = (updateInterval / currentDuration) * 100;
          const newProgress = prev + increment;
          if (newProgress >= 100) {
            handleNext(); 
            return 0;     
          }
          return newProgress;
        });
      }, updateInterval);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentIndex, filteredPhotos, currentDuration]);

  // === 音乐逻辑 ===
  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying && isMusicOn && hasStarted) {
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) playPromise.catch(e => console.log("Audio waiting"));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, isMusicOn, hasStarted, currentMusicIndex]); 

  // === 辅助函数 ===
  const getRandomEffectKey = () => {
    const keys = Object.keys(TRANSITION_EFFECTS);
    return keys[Math.floor(Math.random() * keys.length)];
  };

  const performSwitchLogic = (nextIndex) => {
    setCurrentIndex(nextIndex);
    setProgress(0);
    updateDynamicDuration();
    if (effectMode === 'smart') {
      setSmartCurrentEffect(getRandomEffectKey());
    }
  };
  
  // 图片加载失败处理
  const handleImageError = (photoId) => {
    console.log('图片加载失败:', photoId);
    setBrokenImageIds(prev => new Set([...prev, photoId]));
  };
  
  // 切换月份选择
  const toggleMonth = (month) => {
    setSelectedMonths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(month)) {
        newSet.delete(month);
      } else {
        newSet.add(month);
      }
      return newSet;
    });
  };
  
  // 清除所有选中的月份
  const clearAllMonths = () => {
    setSelectedMonths(new Set());
  };

  // === 交互处理 ===
  const startExperience = () => {
    setHasStarted(true);
    setIsPlaying(true);
    updateDynamicDuration();
  };

  const handleNextMusic = () => {
    const nextIndex = (currentMusicIndex + 1) % MUSIC_PLAYLIST.length;
    setCurrentMusicIndex(nextIndex);
    setStatusMessage(`正在播放: ${MUSIC_PLAYLIST[nextIndex].title}`);
    if (!isMusicOn) setIsMusicOn(true);
  };

  const updateDynamicDuration = () => {
    if (isMusicOn) {
      const currentBpm = MUSIC_PLAYLIST[currentMusicIndex].bpm;
      const beatMs = 60000 / currentBpm;
      const rhythmOptions = [4 * beatMs, 8 * beatMs, 12 * beatMs];
      const randomDuration = rhythmOptions[Math.floor(Math.random() * rhythmOptions.length)];
      setCurrentDuration(randomDuration);
    } else {
      // 使用用户设置的基础时长
      setCurrentDuration(baseDuration * 1000);
    }
  };
  
  // 调整基础时长
  const adjustBaseDuration = (delta) => {
    setBaseDuration(prev => {
      const newDuration = Math.max(0.5, Math.min(20, prev + delta)); // 0.5-20秒范围
      return Math.round(newDuration * 2) / 2; // 确保是0.5的倍数
    });
  };

  const handleNext = () => {
    const nextIndex = (currentIndex + 1) % filteredPhotos.length;
    performSwitchLogic(nextIndex);
  };

  const handlePrev = () => {
    const nextIndex = (currentIndex - 1 + filteredPhotos.length) % filteredPhotos.length;
    performSwitchLogic(nextIndex);
  };

  const jumpToPhoto = (index) => {
    performSwitchLogic(index);
  };

  const cycleEffect = () => {
    const availableEffects = Object.keys(TRANSITION_EFFECTS);
    const allModes = [...availableEffects, 'smart'];
    const currentModeIndex = allModes.indexOf(effectMode);
    const nextIndex = (currentModeIndex + 1) % allModes.length;
    const nextMode = allModes[nextIndex];
    
    setEffectMode(nextMode);
    if (nextMode === 'smart') {
      setStatusMessage('✨ 智能节奏模式');
      setSmartCurrentEffect(getRandomEffectKey());
    } else {
      setStatusMessage(`${TRANSITION_EFFECTS[nextMode].label}模式`);
    }
  };

  // === 触摸逻辑 ===
  const minSwipeDistance = 50;
  const onTouchStart = (e) => {
    setTouchEnd(null); 
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) handleNext();
    else if (distance < -minSwipeDistance) handlePrev();
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setIsPlaying(false);
    onClose?.();
  };

  // 加载中状态
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mb-4"></div>
        <p className="text-gray-400">加载中...</p>
      </div>
    );
  }
  
  // 空数据处理
  if (!sortedPhotos || sortedPhotos.length === 0) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center text-white">
        <Calendar size={48} className="text-gray-500 mb-4" />
        <p className="text-gray-400 mb-6">暂无照片</p>
        <button 
          onClick={handleClose}
          className="px-6 py-2 bg-gray-800 rounded-full text-sm hover:bg-gray-700"
        >
          返回
        </button>
      </div>
    );
  }

  const currentPhoto = filteredPhotos[currentIndex] || {}; 
  const activeMusic = MUSIC_PLAYLIST[currentMusicIndex];
  const activeEffectKey = effectMode === 'smart' ? smartCurrentEffect : effectMode;
  const currentEffect = TRANSITION_EFFECTS[activeEffectKey];

  return (
    <div className={`fixed inset-0 z-50 bg-black overflow-hidden font-sans select-none transition-all duration-300`}>
      
      <audio key={activeMusic.id} ref={audioRef} src={activeMusic.url} loop />

      {/* 关闭按钮 */}
      <button 
        onClick={handleClose}
        className="absolute top-4 right-4 z-50 p-2 rounded-full bg-black/50 text-white/80 hover:text-white hover:bg-black/70 transition"
      >
        <X size={24} />
      </button>

      {/* 启动遮罩已移除 - 直接自动播放 */}

      {/* 顶部控制栏 */}
      <div className="absolute top-0 left-0 right-0 z-30 flex flex-col bg-gradient-to-b from-black/80 via-black/40 to-transparent pb-8 pt-2">
        {/* 第一行：状态与操作 */}
        <div className="flex justify-between items-center px-4 py-2" style={{ paddingLeft: showTimeline ? 'calc(16rem + 1rem)' : '1rem' }}>
          <div className="flex items-center gap-3">
            {/* 时间轴切换按钮 */}
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="p-2 rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-white/20 transition"
              title={showTimeline ? '隐藏时间轴' : '显示时间轴'}
            >
              <Calendar size={18} />
            </button>
            {statusMessage && (
              <div className="text-white/90 text-sm font-medium tracking-wide flex items-center gap-2 overflow-hidden max-w-[120px]">
                <span className="bg-white/20 px-2 py-1 rounded text-xs backdrop-blur-sm border border-white/10 truncate">
                  {statusMessage}
                </span>
              </div>
            )}
          </div>
          
          <div className="flex gap-1 items-center bg-black/20 backdrop-blur-md rounded-full px-2 py-1">
            {/* 时长控制 */}
            <div className="flex items-center gap-1 px-2">
              <button 
                onClick={() => adjustBaseDuration(-0.5)}
                className="text-white/70 hover:text-white p-1 text-xs font-bold"
                disabled={baseDuration <= 0.5}
              >
                -
              </button>
              <span className="text-white/90 text-xs font-mono min-w-[2.5rem] text-center">
                {baseDuration}s
              </span>
              <button 
                onClick={() => adjustBaseDuration(0.5)}
                className="text-white/70 hover:text-white p-1 text-xs font-bold"
                disabled={baseDuration >= 20}
              >
                +
              </button>
            </div>

             <button onClick={toggleFullScreen} className="text-white/80 hover:text-white p-1.5">
              {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          </div>
        </div>

        {/* 第二行：课程标签筛选器 */}
        <div className="px-4 overflow-x-auto no-scrollbar mt-2">
          <div className="flex gap-2">
            {availableTags.map(tag => (
              <button
                key={tag}
                onClick={() => setSelectedTag(tag)}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  selectedTag === tag 
                    ? 'bg-yellow-500 text-black shadow-lg scale-105' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                }`}
              >
                {tag !== '全部' && <Tag size={10} className={selectedTag === tag ? 'text-black' : 'text-current'} />}
                {tag}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 图片展示区域 */}
      <div 
        className="relative w-full h-full flex items-center justify-center bg-gray-900 pt-20 pb-40"
        style={{ 
          perspective: '1000px',
          paddingLeft: showTimeline ? '16rem' : '0'
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {filteredPhotos.length > 0 ? (
          filteredPhotos.map((photo, index) => {
            const isActive = index === currentIndex;
            const transitionClass = isActive ? currentEffect.active : currentEffect.inactive;
            
            return (
              <div
                key={`${photo.id}-${index}`}
                className={`absolute inset-0 transition-all duration-1000 ease-in-out transform-gpu origin-center ${transitionClass}`}
                style={{
                  paddingTop: '5rem',
                  paddingBottom: '10rem',
                  paddingLeft: showTimeline ? '16rem' : '0'
                }}
              >
                <img 
                  src={photo.url} 
                  alt={photo.description || '照片'}
                  onError={() => handleImageError(photo.id)}
                  className={`w-full h-full object-contain transition-transform ease-linear ${
                    isActive && isPlaying 
                      ? (isMusicOn ? 'duration-[8000ms] scale-105' : 'duration-[10000ms] scale-105')
                      : 'scale-100'
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/10" />
              </div>
            );
          })
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/50">
            该分类下暂无照片
          </div>
        )}
      </div>

      {/* 信息展示层 */}
      {filteredPhotos.length > 0 && (
        <div 
          className="absolute bottom-44 left-0 right-0 z-20 px-6 text-white pointer-events-none"
          style={{ paddingLeft: showTimeline ? 'calc(16rem + 1.5rem)' : '1.5rem' }}
        >
          <div className="overflow-hidden mb-2">
            <div className="flex items-center gap-2 text-yellow-400 font-bold text-lg mb-1" key={`date-${currentPhoto.id}`}>
              <Calendar size={18} />
              <span>{currentPhoto.date ? new Date(currentPhoto.date).toISOString().split('T')[0] : ''}</span>
              {currentPhoto.course_name && (
                <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded ml-2 border border-yellow-500/30">
                  {currentPhoto.course_name}
                </span>
              )}
            </div>
          </div>
          {currentPhoto.description && (
            <p className="text-gray-100 text-base leading-snug line-clamp-2 opacity-90">
              {currentPhoto.description}
            </p>
          )}
        </div>
      )}

      {/* 左侧时间轴侧边栏 */}
      {showTimeline && (
        <div className="absolute left-0 top-0 bottom-0 w-64 bg-black/70 backdrop-blur-md border-r border-white/10 z-30 flex flex-col">
          <div className="p-4 flex-shrink-0 border-b border-white/10">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Calendar size={20} />
                时间轴
              </h3>
              <button 
                onClick={() => setShowTimeline(false)}
                className="text-white/60 hover:text-white p-1"
              >
                <ChevronLeft size={20} />
              </button>
            </div>
            {selectedMonths.size > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-xs text-white/60">已选 {selectedMonths.size} 个月</span>
                <button
                  onClick={clearAllMonths}
                  className="text-xs text-yellow-400 hover:text-yellow-300 transition"
                >
                  清除所有
                </button>
              </div>
            )}
          </div>
          
          {/* 年月选择列表 */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-1">
              {availableMonths.map((month) => {
                const isSelected = selectedMonths.has(month);
                const displayMonth = formatMonthToYYMM(month);
                
                return (
                  <button
                    key={month}
                    onClick={() => toggleMonth(month)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      isSelected 
                        ? 'bg-yellow-500 text-black shadow-lg shadow-yellow-500/20' 
                        : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono tracking-wider">{displayMonth}</span>
                      {isSelected && (
                        <span className="text-xs">✓</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {availableMonths.length === 0 && (
              <div className="text-center text-white/50 text-sm py-8">
                暂无可用月份
              </div>
            )}
          </div>
        </div>
      )}
      {/* 时间轴切换按钮已移至顶部控制栏 */}

      {/* 底部控制栏 */}
      <div className="absolute bottom-0 left-0 right-0 z-30 bg-black/40 backdrop-blur-md pt-2 pb-6 px-4 border-t border-white/10">
        <div className="w-full h-1 bg-gray-600 rounded-full mb-4 overflow-hidden">
          <div className="h-full bg-yellow-500 transition-all duration-100 ease-linear rounded-full" style={{ width: `${progress}%` }} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => setIsPlaying(!isPlaying)} className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center hover:bg-gray-200 active:scale-95 transition">
              {isPlaying ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5"/>}
            </button>
            <div className="flex gap-2">
              <button onClick={handlePrev} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition" disabled={filteredPhotos.length <= 1}><ChevronLeft size={24} /></button>
              <button onClick={handleNext} className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition" disabled={filteredPhotos.length <= 1}><ChevronRight size={24} /></button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar max-w-[120px]">
             {filteredPhotos.map((photo, index) => (
               <button key={`thumb-${photo.id}-${index}`} onClick={() => jumpToPhoto(index)} className={`relative flex-shrink-0 w-8 h-8 rounded overflow-hidden border-2 transition ${index === currentIndex ? 'border-yellow-500 scale-110' : 'border-transparent opacity-50'}`}>
                 <img src={photo.url} alt="thumbnail" className="w-full h-full object-cover" onError={() => handleImageError(photo.id)} />
               </button>
             ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.5s ease-out forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .-rotate-y-90 { transform: rotateY(-90deg); }
        .rotate-y-0 { transform: rotateY(0deg); }
      `}</style>
    </div>
  );
}
